import { useEffect, useEffectEvent, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { EdgeHandle } from '@components/ui/EdgeHandle/EdgeHandle';
import { CalendarIcon, ChevronRightIcon } from '@utils/icons';
import { zonedDay } from '../logistics.format';
import { CalendarView, type ICalendarViewProps } from './CalendarView';
import { DAY_LIST_SELECTOR } from './CalendarDayList';
import { useCalendarPeriod } from './useCalendarPeriod';
import { useLegendFilter } from './useLegendFilter';
import styles from './DeliveryCalendar.module.css';

interface IDeliveryCalendarProps extends Omit<ICalendarViewProps, 'period' | 'filter' | 'today'> {
  /** Something above the calendar is open (a drawer, a dialog, the tour): Esc
   *  belongs to it. */
  escapeTaken: boolean;
}

/**
 * The half circle on the page's right edge and the calendar it pulls out, up to
 * the sidebar. Portalled so it pins to the viewport, not the page's scroller.
 * The view is mounted only while it shows, so a closed calendar adds nothing to
 * the page's renders; it unmounts once the slide-out has finished.
 */
export function DeliveryCalendar({ escapeTaken, ...view }: IDeliveryCalendarProps) {
  const { t } = useTranslation();
  const today = zonedDay(new Date().toISOString(), view.tz);
  const period = useCalendarPeriod(today);
  const filter = useLegendFilter();
  const handleRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  if (open && !mounted) setMounted(true);

  const onKeyDown = useEffectEvent((event: KeyboardEvent) => {
    // An open day list takes the press first, as an overlay of the calendar's own;
    // one already closing doesn't.
    if (event.key !== 'Escape' || escapeTaken || document.querySelector(DAY_LIST_SELECTOR)) return;
    setOpen(false);
    handleRef.current?.focus();
  });
  useEffect(() => {
    if (!open) return;
    /* Capture: read `escapeTaken` before an overlay's own Esc handling closes
       it, or the one key press would close that overlay and the calendar. */
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [open]);

  return createPortal(
    <>
      <aside
        className={styles.panel}
        data-open={open}
        inert={!open}
        aria-label={t('logistics.calendar.title')}
        onTransitionEnd={(event) => {
          if (!open && event.target === event.currentTarget && event.propertyName === 'transform') setMounted(false);
        }}
      >
        {mounted ? <CalendarView {...view} period={period} filter={filter} today={today} /> : null}
      </aside>
      <EdgeHandle
        ref={handleRef}
        className={styles.handle}
        data-open={open}
        aria-expanded={open}
        aria-label={t(open ? 'logistics.calendar.close' : 'logistics.calendar.open')}
        onClick={() => setOpen(!open)}
      >
        {open ? <ChevronRightIcon size={16} /> : <CalendarIcon size={16} />}
      </EdgeHandle>
    </>,
    document.body,
  );
}
